<script setup lang="ts" generic="T extends IntakeFormData = IntakeFormData">
import { computed, ref, unref, watch } from 'vue'
import type { IntakeDocument, IntakeDocumentSlot, IntakeServiceGroup } from '@/api/schemas'
import UploadList from './UploadList.vue'
import NextSteps from './NextSteps.vue'
import FieldError from './FieldError.vue'
import HoursEditor from './HoursEditor.vue'
import AmountInput from './AmountInput.vue'
import AddressFields from './AddressFields.vue'
import { applyMask, dunsMask, einMask, maxLength, phoneMask } from '@/lib/inputFormat'
import { vOpenPicker } from '@/lib/openPicker'
import { matchUsState, usStates } from './usStates'
import {
  addressFieldWarnings,
  entityTypes,
  incompleteSteps,
  missingRecommendedSlots,
  missingRequiredSlots,
  otherFiles,
  unwantedJobTypes,
  wantedJobTypes,
  type BurnsDefaults,
  type BusinessHours,
  type FieldValidation,
  type IntakeFormData,
  type IntakeFormDefinition,
  type IntakeStepDefinition,
} from './formDefinitions'

const props = defineProps<{
  step: IntakeStepDefinition
  stepIndex: number
  definition: IntakeFormDefinition
  documents: IntakeDocument[]
  /** Every document slot for this definition, unfiltered; the review panel needs the full set. */
  documentSlots: IntakeDocumentSlot[]
  savedSteps: Set<number>
  defaults: BurnsDefaults
  services: IntakeServiceGroup[]
  fieldValidation: (key: string) => FieldValidation | undefined
}>()
const emit = defineEmits<{
  upload: [kind: string, event: Event]
  remove: [id: number]
  edit: [step: number]
}>()
const currentStepSlots = computed(() =>
  props.documentSlots.filter((slot) => slot.step === props.stepIndex),
)
const stillNeededLabels = computed(() =>
  missingRequiredSlots(props.documentSlots, props.documents).map((slot) => slot.label),
)
const worthAddingLabels = computed(() =>
  missingRecommendedSlots(props.documentSlots, props.documents).map((slot) => slot.label),
)
const form = defineModel<T>('form', { required: true })

const profileChecks = [
  ['gbp_exists', 'Profile exists'],
  ['gbp_verified', 'Profile is verified'],
  ['gbp_public', 'Profile is public'],
] as const

type BoolFieldName =
  'gbp_exists' | 'gbp_verified' | 'gbp_public' | 'gbp_po_box' | 'broad_category_leads'

function bool(name: BoolFieldName, value: string) {
  form.value[name] = value === 'yes'
}
const serviceTotal = computed(() =>
  props.services.reduce((total, group) => total + group.services.length, 0),
)
const serviceCount = computed(() => form.value.wanted_services.length)
function groupChecked(group: IntakeServiceGroup) {
  return group.services.every((service) => form.value.wanted_services.includes(service.id))
}
function toggleGroup(group: IntakeServiceGroup) {
  const ids = group.services.map((service) => service.id)
  const wanted = form.value.wanted_services
  form.value.wanted_services = groupChecked(group)
    ? wanted.filter((id) => !ids.includes(id))
    : [...wanted, ...ids.filter((id) => !wanted.includes(id))]
}
function addOwner() {
  form.value.owners.push({ full_name: '', title: '', email: '' })
}
function removeOwner(index: number) {
  form.value.owners.splice(index, 1)
}
function addLicense() {
  form.value.licenses.push({ jurisdiction: '', type: '', number: '', expiry: '' })
}
function addLocalRequirement() {
  form.value.local_requirements.push({
    jurisdiction: '',
    work_accepted: '',
    registration_required: undefined,
    registration_held: undefined,
    authority: '',
    number: '',
    expiry: '',
  })
}

// State of formation: a select of US states plus "Other…". The saved value
// is always the raw string (a full name, an unmatched legacy value, or —
// while "Other" is open — whatever the visitor is typing), never rewritten
// until the visitor picks a different option.
const otherStateOpen = ref(false)
const formationStateChoice = computed(() => {
  if (otherStateOpen.value) return 'Other'
  const matched = matchUsState(form.value.formation_state)
  if (matched !== undefined) return matched
  return (form.value.formation_state ?? '') === '' ? '' : 'Other'
})
function chooseFormationState(value: string) {
  if (value === 'Other') {
    otherStateOpen.value = true
    if (matchUsState(form.value.formation_state) !== undefined) form.value.formation_state = ''
    return
  }
  otherStateOpen.value = false
  form.value.formation_state = value
}

// Ad hours can mirror business hours. The choice isn't stored: a saved form
// whose ad hours equal its (set) business hours reopens with the box ticked.
function copyHours(rows: BusinessHours[]): BusinessHours[] {
  return rows.map((row) => ({ ...row }))
}
const adHoursSameAsBusiness = ref(
  form.value.business_hours.some((row) => Boolean(row.opens) || Boolean(row.closes)) &&
    JSON.stringify(form.value.business_hours) === JSON.stringify(form.value.ad_hours),
)
function setAdHoursSameAsBusiness(same: boolean) {
  adHoursSameAsBusiness.value = same
  if (same) form.value.ad_hours = copyHours(form.value.business_hours)
}
watch(
  () => form.value.business_hours,
  (rows) => {
    if (adHoursSameAsBusiness.value) form.value.ad_hours = copyHours(rows)
  },
  { deep: true },
)

// Field-level validation display. An error shows only once its field is
// touched (blur/change/focusout, wired centrally in LsaIntake.vue), and
// disappears the moment the field is valid again. `fieldValidation` reads the useVuelidate()
// instance for the current step, looked up by the same field names used above.
function fieldError(key: string): string | undefined {
  const field = props.fieldValidation(key)
  if (field?.$error !== true) return undefined
  const first = field.$errors[0]
  return first === undefined ? undefined : unref(first.$message)
}
function ariaInvalid(key: string) {
  return fieldError(key) === undefined ? undefined : 'true'
}
function describedBy(key: string) {
  const ids = [fieldError(key) === undefined ? undefined : `err-${key}`].filter(
    (id) => id !== undefined,
  )
  return ids.length > 0 ? ids.join(' ') : undefined
}

// Address warnings never block; AddressFields decides when to show them.
const addressWarnings = computed(() => addressFieldWarnings(form.value))

const incomplete = computed(() => incompleteSteps(props.definition, form.value))
</script>

<template>
  <fieldset v-if="step.kind === 'prerequisites'">
    <legend>Does a Google Business Profile exist, is verified, and is public?</legend>
    <label v-for="[name, label] in profileChecks" :key="name" class="choice"
      ><span>{{ label }}</span
      ><select
        :id="'field-' + name"
        :value="form[name] === undefined ? '' : form[name] ? 'yes' : 'no'"
        :aria-invalid="ariaInvalid(name)"
        :aria-describedby="describedBy(name)"
        @change="bool(name, ($event.target as HTMLSelectElement).value)"
      >
        <option value="">Choose one</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      <FieldError :id="'err-' + name" :message="fieldError(name)" />
    </label>
    <AddressFields
      v-model:form="form"
      prefix="gbp_address"
      legend="Address shown on the profile"
      :field-validation="fieldValidation"
      :warnings="addressWarnings.gbp_address"
    />
    <label class="choice"
      ><span>Is this a P.O. Box?</span
      ><select
        :value="form.gbp_po_box === undefined ? '' : form.gbp_po_box ? 'yes' : 'no'"
        @change="bool('gbp_po_box', ($event.target as HTMLSelectElement).value)"
      >
        <option value="">Choose one</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select></label
    >
  </fieldset>

  <fieldset v-else-if="step.kind === 'identity'">
    <p v-if="definition.id === 'burns-construction'" class="intro">
      Enter the legal entity details exactly as they appear on the documents you upload next.
    </p>
    <label>
      Exact legal name as filed
      <input
        id="field-legal_name"
        v-model="form.legal_name"
        autocomplete="organization"
        autocapitalize="words"
        :maxlength="maxLength.text"
        :aria-invalid="ariaInvalid('legal_name')"
        :aria-describedby="describedBy('legal_name')"
      />
      <FieldError id="err-legal_name" :message="fieldError('legal_name')" />
    </label>
    <label
      >DBA, if any<input v-model="form.dba" autocapitalize="words" :maxlength="maxLength.text"
    /></label>
    <div class="grid">
      <label>
        Entity type
        <select
          id="field-entity_type_choice"
          v-model="form.entity_type_choice"
          :aria-invalid="ariaInvalid('entity_type_choice')"
          :aria-describedby="describedBy('entity_type_choice')"
        >
          <option value="">Choose one</option>
          <option v-for="option in entityTypes" :key="option" :value="option">{{ option }}</option>
        </select>
        <FieldError id="err-entity_type_choice" :message="fieldError('entity_type_choice')" />
      </label>
      <label v-if="form.entity_type_choice === 'Other'">
        Describe the entity type
        <input
          id="field-entity_type_other"
          v-model="form.entity_type_other"
          placeholder="e.g. Inc. or LLP"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('entity_type_other')"
          :aria-describedby="describedBy('entity_type_other')"
        />
        <FieldError id="err-entity_type_other" :message="fieldError('entity_type_other')" />
      </label>
      <label>
        State of formation
        <select
          id="field-formation_state"
          :value="formationStateChoice"
          :aria-invalid="ariaInvalid('formation_state')"
          :aria-describedby="describedBy('formation_state')"
          @change="chooseFormationState(($event.target as HTMLSelectElement).value)"
        >
          <option value="">Choose one</option>
          <option v-for="state in usStates" :key="state.code" :value="state.name">
            {{ state.name }}
          </option>
          <option value="Other">Other…</option>
        </select>
        <input
          v-if="formationStateChoice === 'Other'"
          v-model="form.formation_state"
          data-field="formation_state"
          aria-label="State or country of formation"
          autocapitalize="words"
          :maxlength="maxLength.text"
        />
        <FieldError id="err-formation_state" :message="fieldError('formation_state')" />
      </label>
      <label>
        Date of formation
        <input
          id="field-formation_date"
          v-model="form.formation_date"
          v-open-picker
          type="date"
          :aria-invalid="ariaInvalid('formation_date')"
          :aria-describedby="describedBy('formation_date')"
        />
        <FieldError id="err-formation_date" :message="fieldError('formation_date')" />
      </label>
    </div>
    <label>
      EIN
      <input
        id="field-ein"
        :value="form.ein"
        inputmode="numeric"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        placeholder="12-3456789"
        :aria-invalid="ariaInvalid('ein')"
        :aria-describedby="describedBy('ein')"
        @input="form.ein = applyMask($event, einMask, form.ein)"
      />
      <FieldError id="err-ein" :message="fieldError('ein')" />
    </label>
    <label>
      D-U-N-S number (optional)
      <input
        id="field-duns"
        :value="form.duns"
        inputmode="numeric"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        placeholder="12-345-6789"
        :aria-invalid="ariaInvalid('duns')"
        :aria-describedby="describedBy('duns')"
        @input="form.duns = applyMask($event, dunsMask, form.duns)"
      />
      <FieldError id="err-duns" :message="fieldError('duns')" />
      <small
        >Google checks business registration with this number when it can. Leave it blank if you
        don't have one.</small
      >
    </label>
    <AddressFields
      v-model:form="form"
      prefix="physical_address"
      legend="Physical address"
      :field-validation="fieldValidation"
      :warnings="addressWarnings.physical_address"
    />
    <label class="check">
      <input
        id="field-mailing_same_as_physical"
        v-model="form.mailing_same_as_physical"
        type="checkbox"
      />
      The mailing address is the same as the physical address.
    </label>
    <AddressFields
      v-if="!form.mailing_same_as_physical"
      v-model:form="form"
      prefix="mailing_address"
      legend="Mailing address"
      :field-validation="fieldValidation"
      :warnings="addressWarnings.mailing_address"
    />
    <div class="grid" :class="{ 'single-field': definition.id === 'burns-construction' }">
      <label v-if="definition.id !== 'burns-construction'">
        Main phone
        <input
          id="field-main_phone"
          :value="form.main_phone"
          type="tel"
          autocomplete="tel"
          inputmode="tel"
          spellcheck="false"
          placeholder="417-555-0100"
          :aria-invalid="ariaInvalid('main_phone')"
          :aria-describedby="describedBy('main_phone')"
          @input="form.main_phone = applyMask($event, phoneMask, form.main_phone)"
        />
        <FieldError id="err-main_phone" :message="fieldError('main_phone')" />
      </label>
      <label>
        Business email
        <input
          id="field-business_email"
          v-model="form.business_email"
          type="email"
          autocomplete="email"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('business_email')"
          :aria-describedby="describedBy('business_email')"
        />
        <FieldError id="err-business_email" :message="fieldError('business_email')" />
      </label>
      <label v-if="definition.id !== 'burns-construction'">
        Website
        <input
          id="field-website"
          v-model="form.website"
          type="url"
          autocomplete="url"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text"
          placeholder="example.com"
          :aria-invalid="ariaInvalid('website')"
          :aria-describedby="describedBy('website')"
        />
        <FieldError id="err-website" :message="fieldError('website')" />
      </label>
      <label v-if="definition.id !== 'burns-construction'">
        Years in business
        <AmountInput
          id="field-years_in_business"
          v-model="form.years_in_business"
          kind="whole"
          :aria-invalid="ariaInvalid('years_in_business')"
          :aria-describedby="describedBy('years_in_business')"
        />
        <FieldError id="err-years_in_business" :message="fieldError('years_in_business')" />
      </label>
    </div>
    <template v-if="definition.id === 'burns-construction'">
      <h2>Owners</h2>
      <p class="intro">
        List everyone who owns any share of the business, including family members and partners who
        aren't involved day to day. Use each owner's full legal name, first and last, as it appears
        on their ID: Google matches it exactly.
      </p>
      <p class="callout">
        Google runs a background check on every owner listed here before your ads can run. Each
        owner gets a request from Google's screening partner and completes it with them directly.
        Nothing for the check is collected on this form.
      </p>
      <div data-field="owners" class="owners">
        <div v-for="(owner, index) in form.owners" :key="index" class="grid owner">
          <label
            >Full legal name<input
              :id="index === 0 ? 'field-owners' : undefined"
              v-model="owner.full_name"
              autocomplete="off"
              autocapitalize="words"
              :maxlength="maxLength.text"
              :aria-invalid="index === 0 ? ariaInvalid('owners') : undefined"
              :aria-describedby="index === 0 ? describedBy('owners') : undefined"
          /></label>
          <label
            >Title (optional)<input
              v-model="owner.title"
              placeholder="e.g. Owner, Member"
              autocapitalize="words"
              :maxlength="maxLength.text"
          /></label>
          <label
            >Email (optional)<input
              v-model="owner.email"
              type="email"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              :maxlength="maxLength.text"
          /></label>
          <button
            v-if="form.owners.length > 1"
            type="button"
            class="text-button"
            :aria-label="`Remove owner ${Number(index) + 1}`"
            @click="removeOwner(Number(index))"
          >
            Remove
          </button>
        </div>
        <FieldError id="err-owners" :message="fieldError('owners')" />
      </div>
      <button type="button" class="text-button" @click="addOwner">Add another owner</button>
      <label class="check">
        <input
          id="field-owners_background_check_acknowledged"
          v-model="form.owners_background_check_acknowledged"
          type="checkbox"
          :aria-invalid="ariaInvalid('owners_background_check_acknowledged')"
          :aria-describedby="describedBy('owners_background_check_acknowledged')"
        />
        Every owner listed knows Google will run a background check on them.
      </label>
      <FieldError
        id="err-owners_background_check_acknowledged"
        :message="fieldError('owners_background_check_acknowledged')"
      />
    </template>
  </fieldset>

  <fieldset v-else-if="step.kind === 'owner'">
    <div class="grid">
      <label>
        Owner’s legal name
        <input
          id="field-owner_legal_name"
          v-model="form.owner_legal_name"
          autocomplete="name"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('owner_legal_name')"
          :aria-describedby="describedBy('owner_legal_name')"
        />
        <FieldError id="err-owner_legal_name" :message="fieldError('owner_legal_name')" />
      </label>
      <label
        >Title<input
          v-model="form.title"
          autocomplete="organization-title"
          autocapitalize="words"
          :maxlength="maxLength.text"
      /></label>
      <label>
        Direct phone
        <input
          id="field-direct_phone"
          :value="form.direct_phone"
          type="tel"
          autocomplete="tel"
          inputmode="tel"
          spellcheck="false"
          placeholder="417-555-0100"
          :aria-invalid="ariaInvalid('direct_phone')"
          :aria-describedby="describedBy('direct_phone')"
          @input="form.direct_phone = applyMask($event, phoneMask, form.direct_phone)"
        />
        <FieldError id="err-direct_phone" :message="fieldError('direct_phone')" />
      </label>
      <label>
        Direct email
        <input
          id="field-direct_email"
          v-model="form.direct_email"
          type="email"
          autocomplete="email"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('direct_email')"
          :aria-describedby="describedBy('direct_email')"
        />
        <FieldError id="err-direct_email" :message="fieldError('direct_email')" />
      </label>
    </div>
    <label
      >Who will complete Google’s background check?<input
        v-model="form.background_check_contact"
        autocapitalize="words"
        :maxlength="maxLength.text"
    /></label>
    <p class="callout">
      Google and its screening vendor collect background-check information directly. Do not enter
      date of birth, Social Security number, or driver-license number here.
    </p>
    <label class="check">
      <input
        id="field-background_check_acknowledged"
        v-model="form.background_check_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('background_check_acknowledged')"
        :aria-describedby="describedBy('background_check_acknowledged')"
      />
      I understand the background check is completed directly with Google’s vendor.
    </label>
    <FieldError
      id="err-background_check_acknowledged"
      :message="fieldError('background_check_acknowledged')"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'services'">
    <label
      >Proposed primary LSA category<select v-model="form.primary_lsa_category">
        <option value="">Confirm in live Google onboarding</option>
        <option>General contractor (if offered and accurate)</option>
        <option>Another category — confirm during setup</option>
      </select></label
    >
    <p class="callout">
      These are proposed work descriptions, not Google job-type labels or claims about licensing.
      Select wanted work only when it is insured, staffed, and permitted as applicable.
    </p>
    <div class="checks">
      <div data-field="wanted_job_types">
        <strong>Wanted paid leads</strong>
        <label v-for="(item, index) in wantedJobTypes" :key="item" class="check">
          <input
            :id="index === 0 ? 'field-wanted_job_types' : undefined"
            v-model="form.wanted_job_types"
            type="checkbox"
            :value="item"
            :aria-invalid="index === 0 ? ariaInvalid('wanted_job_types') : undefined"
            :aria-describedby="index === 0 ? describedBy('wanted_job_types') : undefined"
          />
          {{ item }}
        </label>
        <FieldError id="err-wanted_job_types" :message="fieldError('wanted_job_types')" />
      </div>
      <div>
        <strong>Explicitly not wanted</strong
        ><label v-for="item in unwantedJobTypes" :key="item" class="check"
          ><input v-model="form.unwanted_job_types" type="checkbox" :value="item" />
          {{ item }}</label
        >
      </div>
    </div>
    <label
      >Other wanted work<textarea
        v-model="form.other_wanted_work"
        :maxlength="maxLength.longText"
      />
    </label>
    <label
      >Other explicitly not wanted work<textarea
        v-model="form.other_unwanted_work"
        :maxlength="maxLength.longText"
      />
    </label>
    <label
      >Minimum job size<input v-model="form.minimum_job_size" :maxlength="maxLength.text"
    /></label>
    <label
      >Work refused<textarea v-model="form.work_refused" :maxlength="maxLength.longText" />
    </label>
    <label class="choice"
      ><span>Enable broad-category leads? Google says this may produce generic category leads.</span
      ><select
        :value="
          form.broad_category_leads === undefined ? '' : form.broad_category_leads ? 'yes' : 'no'
        "
        @change="bool('broad_category_leads', ($event.target as HTMLSelectElement).value)"
      >
        <option value="">Choose after discussion</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select></label
    >
    <label class="check">
      <input
        id="field-wanted_work_capability_acknowledged"
        v-model="form.wanted_work_capability_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('wanted_work_capability_acknowledged')"
        :aria-describedby="describedBy('wanted_work_capability_acknowledged')"
      />
      Every wanted type is insured, staffed, and permitted as applicable.
    </label>
    <FieldError
      id="err-wanted_work_capability_acknowledged"
      :message="fieldError('wanted_work_capability_acknowledged')"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'service-area'">
    <div class="grid">
      <label>
        Primary city
        <input
          id="field-primary_city"
          v-model="form.primary_city"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('primary_city')"
          :aria-describedby="describedBy('primary_city')"
        />
        <FieldError id="err-primary_city" :message="fieldError('primary_city')" />
      </label>
      <label>
        Maximum drive distance (miles)
        <AmountInput
          id="field-max_drive_distance"
          v-model="form.max_drive_distance"
          kind="whole"
          :aria-invalid="ariaInvalid('max_drive_distance')"
          :aria-describedby="describedBy('max_drive_distance')"
        />
        <FieldError id="err-max_drive_distance" :message="fieldError('max_drive_distance')" />
      </label>
    </div>
    <label>
      ZIP codes (comma or line separated)
      <textarea
        id="field-zip_codes_text"
        v-model="form.zip_codes_text"
        data-tidy="zips"
        spellcheck="false"
        :maxlength="maxLength.longText"
        :aria-invalid="ariaInvalid('zip_codes_text')"
        :aria-describedby="describedBy('zip_codes_text')"
      />
      <FieldError id="err-zip_codes_text" :message="fieldError('zip_codes_text')" />
    </label>
    <label
      >Counties (comma or line separated)<textarea
        v-model="form.counties_text"
        :maxlength="maxLength.longText"
      />
    </label>
    <label
      >Areas or jobs excluded<textarea v-model="form.exclusions" :maxlength="maxLength.longText" />
    </label>
  </fieldset>

  <fieldset v-else-if="step.kind === 'generic-hours'">
    <p class="intro">Set normal answerable hours. LSA calls are recorded.</p>
    <div data-field="hours">
      <HoursEditor v-model="form.hours" first-id="field-hours" name="Answering" />
      <FieldError id="err-hours" :message="fieldError('hours')" />
    </div>
    <label
      >Who answers calls during these hours?<input
        v-model="form.answered_by"
        autocapitalize="words"
        :maxlength="maxLength.text"
    /></label>
    <label
      >After-hours handling<textarea
        v-model="form.after_hours_handling"
        :maxlength="maxLength.longText"
      />
    </label>
    <label class="check">
      <input
        id="field-calls_recorded_acknowledged"
        v-model="form.calls_recorded_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('calls_recorded_acknowledged')"
        :aria-describedby="describedBy('calls_recorded_acknowledged')"
      />
      I understand LSA calls are recorded.
    </label>
    <FieldError
      id="err-calls_recorded_acknowledged"
      :message="fieldError('calls_recorded_acknowledged')"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'budget'">
    <div class="grid">
      <label>
        Weekly budget (USD)
        <AmountInput
          id="field-weekly_budget"
          v-model="form.weekly_budget"
          kind="money"
          :aria-invalid="ariaInvalid('weekly_budget')"
          :aria-describedby="describedBy('weekly_budget')"
        />
        <FieldError id="err-weekly_budget" :message="fieldError('weekly_budget')" />
      </label>
      <label>
        Target leads per week
        <AmountInput
          id="field-target_leads_per_week"
          v-model="form.target_leads_per_week"
          kind="whole"
          :aria-invalid="ariaInvalid('target_leads_per_week')"
          :aria-describedby="describedBy('target_leads_per_week')"
        />
        <FieldError id="err-target_leads_per_week" :message="fieldError('target_leads_per_week')" />
      </label>
      <label>
        Average job value (USD)
        <AmountInput
          id="field-average_job_value"
          v-model="form.average_job_value"
          kind="money"
          :aria-invalid="ariaInvalid('average_job_value')"
          :aria-describedby="describedBy('average_job_value')"
        />
        <FieldError id="err-average_job_value" :message="fieldError('average_job_value')" />
      </label>
    </div>
  </fieldset>

  <fieldset v-else-if="step.kind === 'licenses'">
    <p class="callout">
      List a state-issued specialty, trade, or regulatory license only if it applies to wanted work.
      Do not infer a statewide general-contractor license. A Branson registration does not replace
      permits or other local requirements.
    </p>
    <div v-for="(license, index) in form.licenses" :key="index" class="license grid">
      <label
        >Issuing authority / jurisdiction<input
          v-model="license.jurisdiction"
          placeholder="e.g. City of Branson"
          autocapitalize="words"
          :maxlength="maxLength.text" /></label
      ><label>License type<input v-model="license.type" :maxlength="maxLength.text" /></label
      ><label
        >Number, as printed<input
          v-model="license.number"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text" /></label
      ><label
        >Expiry / renewal date, if printed<input v-model="license.expiry" v-open-picker type="date"
      /></label>
    </div>
    <button type="button" class="text-button" @click="addLicense">Add another license</button>
    <h2>Local requirements where work is accepted</h2>
    <p>
      Requirements vary. Record the current answer for each municipality or county; this does not
      answer it globally.
    </p>
    <div v-for="(local, index) in form.local_requirements" :key="index" class="license grid">
      <label
        >Jurisdiction<input
          v-model="local.jurisdiction"
          autocapitalize="words"
          :maxlength="maxLength.text" /></label
      ><label
        >Work accepted there<input
          v-model="local.work_accepted"
          :maxlength="maxLength.text" /></label
      ><label
        >Registration required?<select
          :value="
            local.registration_required === undefined
              ? ''
              : local.registration_required
                ? 'yes'
                : 'no'
          "
          @change="
            local.registration_required = ($event.target as HTMLSelectElement).value === 'yes'
          "
        >
          <option value="">Unresolved</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select></label
      ><label
        >Registration held?<select
          :value="
            local.registration_held === undefined ? '' : local.registration_held ? 'yes' : 'no'
          "
          @change="local.registration_held = ($event.target as HTMLSelectElement).value === 'yes'"
        >
          <option value="">Unresolved</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select></label
      ><label
        >Authority<input
          v-model="local.authority"
          autocapitalize="words"
          :maxlength="maxLength.text" /></label
      ><label
        >Number, as printed<input
          v-model="local.number"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text" /></label
      ><label>Expiry, if printed<input v-model="local.expiry" v-open-picker type="date" /></label>
    </div>
    <button type="button" class="text-button" @click="addLocalRequirement">
      Add local jurisdiction</button
    ><UploadList
      :slots="currentStepSlots"
      :documents="documents"
      @upload="(kind, event) => emit('upload', kind, event)"
      @remove="(id) => emit('remove', id)"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'insurance'">
    <div class="grid">
      <label>
        General liability carrier
        <input
          id="field-general_liability_carrier"
          v-model="form.general_liability_carrier"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('general_liability_carrier')"
          :aria-describedby="describedBy('general_liability_carrier')"
        />
        <FieldError
          id="err-general_liability_carrier"
          :message="fieldError('general_liability_carrier')"
        />
      </label>
      <label>
        Policy number
        <input
          id="field-policy_number"
          v-model="form.policy_number"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('policy_number')"
          :aria-describedby="describedBy('policy_number')"
        />
        <FieldError id="err-policy_number" :message="fieldError('policy_number')" />
      </label>
      <label
        >Coverage limits<input v-model="form.coverage_limits" :maxlength="maxLength.text"
      /></label>
      <label>
        Policy expiry
        <input
          id="field-policy_expiry"
          v-model="form.policy_expiry"
          v-open-picker
          type="date"
          :aria-invalid="ariaInvalid('policy_expiry')"
          :aria-describedby="describedBy('policy_expiry')"
        />
        <FieldError id="err-policy_expiry" :message="fieldError('policy_expiry')" />
      </label>
    </div>
    <label class="check"
      ><input v-model="form.has_employees" type="checkbox" /> We have employees and need workers’
      compensation details.</label
    >
    <div v-if="form.has_employees" class="grid">
      <label>
        Workers’ comp carrier
        <input
          id="field-workers_comp_carrier"
          v-model="form.workers_comp_carrier"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('workers_comp_carrier')"
          :aria-describedby="describedBy('workers_comp_carrier')"
        />
        <FieldError id="err-workers_comp_carrier" :message="fieldError('workers_comp_carrier')" />
      </label>
      <label>
        Workers’ comp policy number
        <input
          id="field-workers_comp_policy_number"
          v-model="form.workers_comp_policy_number"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('workers_comp_policy_number')"
          :aria-describedby="describedBy('workers_comp_policy_number')"
        />
        <FieldError
          id="err-workers_comp_policy_number"
          :message="fieldError('workers_comp_policy_number')"
        />
      </label>
      <label>
        Workers’ comp expiry
        <input
          id="field-workers_comp_expiry"
          v-model="form.workers_comp_expiry"
          v-open-picker
          type="date"
          :aria-invalid="ariaInvalid('workers_comp_expiry')"
          :aria-describedby="describedBy('workers_comp_expiry')"
        />
        <FieldError id="err-workers_comp_expiry" :message="fieldError('workers_comp_expiry')" />
      </label>
    </div>
    <p class="callout">
      Google sets minimum coverage by category. Upload the current certificate of insurance.
    </p>
    <UploadList
      :slots="currentStepSlots"
      :documents="documents"
      @upload="(kind, event) => emit('upload', kind, event)"
      @remove="(id) => emit('remove', id)"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'documents'">
    <p class="callout">
      Use documents whose business name and address agree where applicable. Do not upload Social
      Security numbers, dates of birth, driver-license numbers, or passwords. Google's identity
      check collects the owner's photo ID directly, so don't upload it here.
    </p>
    <UploadList
      :slots="currentStepSlots"
      :documents="documents"
      :others="otherFiles(documentSlots, documents)"
      @upload="(kind, event) => emit('upload', kind, event)"
      @remove="(id) => emit('remove', id)"
    />
    <label class="check">
      <input v-model="form.local_licenses_confirmed" type="checkbox" />
      The business holds any city or county licenses needed where it works, and everyone doing the
      work is licensed where required. Google asks you to confirm this during setup.
    </label>
    <label class="check">
      <input
        id="field-name_address_consistency_acknowledged"
        v-model="form.name_address_consistency_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('name_address_consistency_acknowledged')"
        :aria-describedby="describedBy('name_address_consistency_acknowledged')"
      />
      I checked the submitted documents for name and address consistency.
    </label>
    <FieldError
      id="err-name_address_consistency_acknowledged"
      :message="fieldError('name_address_consistency_acknowledged')"
    />
    <label
      >Notes for Baker Web Solutions<textarea
        v-model="form.notes"
        :maxlength="maxLength.longText"
      />
    </label>
  </fieldset>

  <fieldset v-else-if="step.kind === 'google-access'">
    <label>
      Google account that should own the LSA account
      <input
        id="field-owner_google_account"
        v-model="form.owner_google_account"
        type="email"
        autocomplete="email"
        autocapitalize="off"
        spellcheck="false"
        :maxlength="maxLength.text"
        :aria-invalid="ariaInvalid('owner_google_account')"
        :aria-describedby="describedBy('owner_google_account')"
      />
      <FieldError id="err-owner_google_account" :message="fieldError('owner_google_account')" />
    </label>
    <ol>
      <li>Sign in to the account above.</li>
      <li>When invited, grant Baker Web Solutions manager access; do not share a password.</li>
      <li>Grant access to the Google Business Profile if it is available.</li>
    </ol>
    <label class="check">
      <input
        id="field-manager_access_acknowledged"
        v-model="form.manager_access_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('manager_access_acknowledged')"
        :aria-describedby="describedBy('manager_access_acknowledged')"
      />
      I understand the manager-access steps.
    </label>
    <FieldError
      id="err-manager_access_acknowledged"
      :message="fieldError('manager_access_acknowledged')"
    />
    <label class="check"
      ><input v-model="form.gbp_access_available" type="checkbox" /> GBP access is available to
      grant.</label
    >
    <label class="check"
      ><input v-model="form.existing_google_ads_account" type="checkbox" /> We already have a Google
      Ads account.</label
    >
    <label v-if="form.existing_google_ads_account"
      >Existing Google Ads account details<textarea
        v-model="form.existing_google_ads_account_details"
        :maxlength="maxLength.longText"
      />
    </label>
  </fieldset>

  <fieldset v-else-if="step.kind === 'burns-services'">
    <p class="intro">
      Check the work you want Google to send you calls for. Anything left unchecked gets no leads.
      You can change this later.
    </p>
    <div data-field="wanted_services">
      <section
        v-for="(group, index) in services"
        :key="group.title"
        class="service-group"
        :aria-labelledby="`service-group-${index}`"
      >
        <header>
          <div>
            <h2 :id="`service-group-${index}`">{{ group.title }}</h2>
            <p class="source">{{ group.source }}</p>
          </div>
          <button type="button" class="text-button" @click="toggleGroup(group)">
            {{ groupChecked(group) ? 'Clear group' : 'Select all' }}
          </button>
        </header>
        <div class="service-list">
          <label
            v-for="(service, serviceIndex) in group.services"
            :key="service.id"
            class="service-row"
          >
            <input
              :id="index === 0 && serviceIndex === 0 ? 'field-wanted_services' : undefined"
              v-model="form.wanted_services"
              type="checkbox"
              :value="service.id"
              :aria-invalid="
                index === 0 && serviceIndex === 0 ? ariaInvalid('wanted_services') : undefined
              "
              :aria-describedby="
                index === 0 && serviceIndex === 0 ? describedBy('wanted_services') : undefined
              "
            />
            <span>{{ service.label }}</span>
          </label>
        </div>
      </section>
      <p class="service-count" aria-live="polite">
        {{ serviceCount }} of {{ serviceTotal }} selected. The other
        {{ serviceTotal - serviceCount }} get no leads.
      </p>
      <FieldError id="err-wanted_services" :message="fieldError('wanted_services')" />
    </div>
    <label
      >Other work you want leads for<textarea
        v-model="form.other_wanted_work"
        :maxlength="maxLength.longText"
      />
    </label>
    <label
      >Smallest job worth a call<input
        v-model="form.minimum_job_size"
        placeholder="Dollar amount or a short description"
        :maxlength="maxLength.text"
    /></label>
    <label class="check">
      <input
        id="field-wanted_work_capability_acknowledged"
        v-model="form.wanted_work_capability_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('wanted_work_capability_acknowledged')"
        :aria-describedby="describedBy('wanted_work_capability_acknowledged')"
      />
      Every checked job is insured and staffed, and permitted where required.
    </label>
    <FieldError
      id="err-wanted_work_capability_acknowledged"
      :message="fieldError('wanted_work_capability_acknowledged')"
    />
  </fieldset>

  <fieldset v-else-if="step.kind === 'burns-contact'">
    <p class="intro">
      Use the phone number that should receive LSA calls. Baker Web Solutions will send lead
      notifications to the Burns Global address already on file.
    </p>
    <label>
      LSA call phone
      <input
        id="field-lsa_call_phone"
        :value="form.lsa_call_phone"
        type="tel"
        autocomplete="tel"
        inputmode="tel"
        spellcheck="false"
        placeholder="417-555-0100"
        :aria-invalid="ariaInvalid('lsa_call_phone')"
        :aria-describedby="describedBy('lsa_call_phone')"
        @input="form.lsa_call_phone = applyMask($event, phoneMask, form.lsa_call_phone)"
      />
      <FieldError id="err-lsa_call_phone" :message="fieldError('lsa_call_phone')" />
    </label>
  </fieldset>

  <fieldset v-else-if="step.kind === 'burns-hours'">
    <p class="intro">
      Set normal business hours and the hours when LSA ads should run. Keep ad hours to times
      someone will pick up: Google ranks businesses partly on how reliably they answer, and missed
      calls can push you down. Outside ad hours, customers can still send you a message.
    </p>
    <h2>Business hours</h2>
    <div data-field="business_hours">
      <HoursEditor v-model="form.business_hours" first-id="field-business_hours" name="Business" />
      <FieldError id="err-business_hours" :message="fieldError('business_hours')" />
    </div>
    <h2>LSA ad hours</h2>
    <label class="check">
      <input
        type="checkbox"
        :checked="adHoursSameAsBusiness"
        @change="setAdHoursSameAsBusiness(($event.target as HTMLInputElement).checked)"
      />
      Same as business hours
    </label>
    <div v-if="!adHoursSameAsBusiness" data-field="ad_hours">
      <HoursEditor v-model="form.ad_hours" first-id="field-ad_hours" name="Ad" />
      <FieldError id="err-ad_hours" :message="fieldError('ad_hours')" />
    </div>
  </fieldset>

  <fieldset v-else>
    <p class="intro">
      Review each saved step. You can go back to correct or complete anything. Submitting sends the
      saved intake to Baker Web Solutions.
    </p>
    <dl class="review">
      <template v-for="(item, index) in definition.steps.slice(0, -1)" :key="item.title">
        <dt>{{ item.title }}</dt>
        <dd>
          {{ savedSteps.has(index) ? 'Saved' : 'Not saved yet' }}
          <span v-if="incomplete.includes(index)" class="needs-attention">Needs attention</span>
          <button
            v-if="incomplete.includes(index)"
            type="button"
            class="text-button complete-step"
            @click="emit('edit', index)"
          >
            Complete this step
          </button>
          <button
            v-else-if="savedSteps.has(index)"
            type="button"
            class="text-button"
            @click="emit('edit', index)"
          >
            Edit
          </button>
        </dd>
      </template>
    </dl>
    <div class="documents-panel">
      <p v-if="stillNeededLabels.length">
        Still needed: {{ stillNeededLabels.join(', ') }}. You can submit now and add these later
        from this same link.
      </p>
      <p v-else>Nothing required is missing.</p>
      <p v-if="worthAddingLabels.length">
        Worth adding if you have them: {{ worthAddingLabels.join(', ') }}.
      </p>
    </div>
    <NextSteps :review-url="defaults.review_url" />
    <label class="check">
      <input
        id="field-review_acknowledged"
        v-model="form.review_acknowledged"
        type="checkbox"
        :aria-invalid="ariaInvalid('review_acknowledged')"
        :aria-describedby="describedBy('review_acknowledged')"
      />
      I reviewed this intake and confirm the information is accurate to the best of my knowledge.
    </label>
    <FieldError id="err-review_acknowledged" :message="fieldError('review_acknowledged')" />
    <label
      >Final notes<textarea v-model="form.review_notes" :maxlength="maxLength.longText" />
    </label>
  </fieldset>
</template>

<style scoped>
/* These controls live in this component, so their visual system must live here
   too. Parent scoped styles cannot select through a Vue component boundary. */
fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 1rem;
}
legend {
  font-weight: 700;
  margin-bottom: 1rem;
}
h2 {
  color: var(--pine);
  font-size: 1.05rem;
  margin: 0.5rem 0 -0.25rem;
}
p {
  line-height: 1.5;
}
.intro {
  color: #526057;
  margin: 0;
  max-width: 62ch;
}
.callout {
  background: #f0f3dd;
  border-left: 4px solid #83902e;
  padding: 0.8rem 1rem;
  margin: 0.1rem 0;
  line-height: 1.45;
}
label {
  display: grid;
  gap: 0.35rem;
  font-size: 0.93rem;
  font-weight: 650;
}
input,
select,
textarea {
  box-sizing: border-box;
  width: 100%;
  padding: 0.62rem 0.7rem;
  border: 1px solid #aeb9ac;
  border-radius: 3px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-weight: 400;
}
textarea {
  min-height: 5.5rem;
  resize: vertical;
}
input:focus-visible,
select:focus-visible,
textarea:focus-visible,
button:focus-visible {
  outline: 3px solid #b9c967;
  outline-offset: 2px;
  border-color: var(--pine);
}
input:disabled {
  background: #edf0ea;
  color: #68746c;
  cursor: not-allowed;
}
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.grid.single-field {
  grid-template-columns: minmax(0, 1fr);
}
.choice {
  grid-template-columns: 1fr minmax(120px, 180px);
  align-items: center;
}
.check {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-weight: 400;
  line-height: 1.4;
}
.check input {
  width: auto;
  margin-top: 0.28rem;
  accent-color: var(--pine);
}
.checks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}
.checks > div {
  display: grid;
  gap: 0.55rem;
}
.text-button {
  align-self: start;
  width: fit-content;
  padding: 0;
  border: 0;
  background: none;
  color: var(--pine);
  font: inherit;
  font-weight: 650;
  text-decoration: underline;
  cursor: pointer;
}
.review {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.65rem 1rem;
  margin: 0;
}
.review dt {
  font-weight: 650;
}
.review dd {
  margin: 0;
  color: #526057;
}
.service-group {
  border: 1px solid var(--line, #cad2c4);
  background: #fff;
}
.service-group header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e6df;
  background: #f4f6f0;
}
.service-group h2 {
  margin: 0;
}
.service-group header .text-button {
  white-space: nowrap;
}
.service-group .source {
  margin: 0.15rem 0 0;
  font-size: 0.82rem;
  color: #526057;
}
.service-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.service-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-height: 2.9rem;
  padding: 0.45rem 1rem;
  font-weight: 400;
  line-height: 1.35;
  border-bottom: 1px solid #eef1eb;
  cursor: pointer;
}
.service-row:nth-child(odd) {
  border-right: 1px solid #eef1eb;
}
.service-row:hover {
  background: #f7f9f3;
}
.service-row:has(input:checked) {
  background: #eef3dc;
  font-weight: 650;
}
.service-row input {
  width: 1.15rem;
  height: 1.15rem;
  margin: 0;
  flex: none;
  accent-color: var(--pine);
}
.service-count {
  margin: 0;
  font-size: 0.9rem;
  color: #526057;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.field-error {
  grid-column: 1 / -1;
  margin: -0.2rem 0 0;
  color: #a53c28;
  font-size: 0.85rem;
  font-weight: 650;
  line-height: 1.4;
}
.field-error span {
  margin-right: 0.25rem;
}
.field-warning {
  grid-column: 1 / -1;
  margin: -0.2rem 0 0;
  color: #6b5310;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.4;
}
input:not([type='checkbox'])[aria-invalid='true'],
select[aria-invalid='true'],
textarea[aria-invalid='true'] {
  border-color: #a53c28;
  box-shadow: 0 0 0 1px #a53c28;
}
.owners {
  display: grid;
  gap: 0.8rem;
}
.owner {
  grid-template-columns: 2fr 1fr 2fr auto;
  align-items: end;
}
.needs-attention {
  color: #a53c28;
  font-weight: 650;
  margin-left: 0.4rem;
}
.documents-panel {
  background: #f4f6f0;
  border: 1px solid var(--line);
  padding: 0.8rem 1rem;
  display: grid;
  gap: 0.4rem;
}
.documents-panel p {
  margin: 0;
  font-size: 0.9rem;
  color: #3b4a3d;
}
label small {
  display: block;
  font-size: 0.82rem;
  font-weight: 400;
  color: #526057;
}
@media (max-width: 600px) {
  .grid,
  .checks,
  .choice,
  .service-list {
    grid-template-columns: 1fr;
  }
  .service-row:nth-child(odd) {
    border-right: 0;
  }
  .review {
    grid-template-columns: 1fr;
  }
}
</style>
